import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PdfPreviewModal from './components/PdfPreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// ============ Composants de formulaire ============

const NumInput = ({ value, onChange, placeholder, testid }) => (
  <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : +e.target.value)}
         placeholder={placeholder || '0'} className="realease-input text-sm" data-testid={testid} />
);

const TextInput = ({ value, onChange, placeholder, testid }) => (
  <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
         placeholder={placeholder} className="realease-input text-sm" data-testid={testid} />
);

const Section = ({ title, children, testid }) => (
  <div className="realease-card p-5 mb-4" data-testid={testid}>
    <h4 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">{title}</h4>
    {children}
  </div>
);

// ============ MAIN ============

export const FinancingDossierSection = ({ projectId }) => {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const fetchDossier = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/financing-dossiers/${projectId}`);
      setDossier(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchDossier(); }, [fetchDossier]);
  useEffect(() => {
    axios.get(`${API}/analyses`).then(r => setAnalyses(r.data || [])).catch(() => {});
  }, []);

  const save = async (silent = false) => {
    if (!dossier) return;
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        analysis_id: dossier.analysis_id || null,
        borrowers: dossier.borrowers || [],
        savings: dossier.savings || {},
        charges: dossier.charges || {},
        existing_loans: dossier.existing_loans || [],
        existing_rentals: dossier.existing_rentals || [],
        weight_rental_income: dossier.weight_rental_income !== false,
        notes: dossier.notes || null,
      };
      const r = await axios.post(`${API}/financing-dossiers/${projectId}`, payload);
      setDossier(r.data);
      if (!silent) alert('Dossier de financement sauvegarde.');
    } catch (e) {
      alert('Erreur sauvegarde : ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const downloadPdf = async (engine = 'auto') => {
    try {
      await save(true);
    } catch (e) {
      // save() has its own alert - don't block the PDF flow
      console.warn('save before pdf failed', e);
    }
    try {
      const url_q = engine === 'fallback' ? '?engine=fallback' : '';
      const r = await axios.get(`${API}/financing-dossiers/${projectId}/pdf${url_q}`, { responseType: 'blob' });
      const contentType = r.headers?.['content-type'] || r.headers?.['Content-Type'] || '';
      const blob = new Blob([r.data], { type: 'application/pdf' });
      // Si ce n'est pas un PDF (detecte par content-type), on essaie de lire le texte
      if (!contentType.startsWith('application/pdf') && blob.size < 10000) {
        const text = await blob.text();
        try {
          const j = JSON.parse(text);
          throw new Error(j.detail || 'Erreur inconnue');
        } catch (parseErr) {
          throw new Error(text.slice(0, 300) || 'Reponse PDF invalide');
        }
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = engine === 'fallback' ? '_apercu' : '';
      a.download = `RealEase_Dossier_Financement_${projectId}${suffix}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e.message || 'Erreur inconnue';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          try {
            const j = JSON.parse(text);
            msg = j.detail || text.slice(0, 300);
          } catch { msg = text.slice(0, 300) || `HTTP ${e.response.status}`; }
        } catch {
          msg = `HTTP ${e.response?.status || '??'} - reponse illisible`;
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      console.error('[PDF download error]', e, msg);
      alert('Erreur telechargement : ' + msg);
    }
  };

  const sendToClient = async () => {
    if (!window.confirm('Envoyer le dossier au client par email ?')) return;
    try {
      await save(true);
      const r = await axios.post(`${API}/financing-dossiers/${projectId}/send-to-client`);
      alert(`Envoye a ${r.data.recipient}`);
    } catch (e) { alert('Erreur envoi : ' + (e.response?.data?.detail || e.message)); }
  };

  const addBorrower = () => {
    if ((dossier.borrowers || []).length >= 2) { alert('Maximum 2 emprunteurs'); return; }
    const ns = [...(dossier.borrowers || []), {}];
    setDossier({ ...dossier, borrowers: ns });
  };
  const removeBorrower = (i) => {
    setDossier({ ...dossier, borrowers: (dossier.borrowers || []).filter((_, idx) => idx !== i) });
  };
  const updBorrower = (i, k, v) => {
    const ns = [...(dossier.borrowers || [])];
    ns[i] = { ...ns[i], [k]: v };
    setDossier({ ...dossier, borrowers: ns });
  };

  const updField = (path, value) => {
    const next = { ...dossier };
    if (path.includes('.')) {
      const [parent, child] = path.split('.');
      next[parent] = { ...(next[parent] || {}), [child]: value };
    } else {
      next[path] = value;
    }
    setDossier(next);
  };

  const addLoan = () => setDossier({ ...dossier, existing_loans: [...(dossier.existing_loans || []), { label: '', monthly_payment: 0, remaining_capital: 0 }] });
  const updLoan = (i, k, v) => {
    const arr = [...(dossier.existing_loans || [])];
    arr[i] = { ...arr[i], [k]: v };
    setDossier({ ...dossier, existing_loans: arr });
  };
  const removeLoan = (i) => setDossier({ ...dossier, existing_loans: (dossier.existing_loans || []).filter((_, idx) => idx !== i) });

  const addRental = () => setDossier({ ...dossier, existing_rentals: [...(dossier.existing_rentals || []), { address: '', monthly_rent: 0 }] });
  const updRental = (i, k, v) => {
    const arr = [...(dossier.existing_rentals || [])];
    arr[i] = { ...arr[i], [k]: v };
    setDossier({ ...dossier, existing_rentals: arr });
  };
  const removeRental = (i) => setDossier({ ...dossier, existing_rentals: (dossier.existing_rentals || []).filter((_, idx) => idx !== i) });

  // Savings custom items (liste dynamique libre)
  const savingsItems = dossier?.savings?.custom_items || [];
  const addSavingItem = () => {
    const next = { ...(dossier.savings || {}) };
    next.custom_items = [...savingsItems, { label: '', amount: 0 }];
    setDossier({ ...dossier, savings: next });
  };
  const updSavingItem = (i, k, v) => {
    const next = { ...(dossier.savings || {}) };
    const arr = [...savingsItems];
    arr[i] = { ...arr[i], [k]: v };
    next.custom_items = arr;
    setDossier({ ...dossier, savings: next });
  };
  const removeSavingItem = (i) => {
    const next = { ...(dossier.savings || {}) };
    next.custom_items = savingsItems.filter((_, idx) => idx !== i);
    setDossier({ ...dossier, savings: next });
  };

  // Charges custom items (liste dynamique libre)
  const chargeItems = dossier?.charges?.custom_items || [];
  const addChargeItem = () => {
    const next = { ...(dossier.charges || {}) };
    next.custom_items = [...chargeItems, { label: '', amount: 0 }];
    setDossier({ ...dossier, charges: next });
  };
  const updChargeItem = (i, k, v) => {
    const next = { ...(dossier.charges || {}) };
    const arr = [...chargeItems];
    arr[i] = { ...arr[i], [k]: v };
    next.custom_items = arr;
    setDossier({ ...dossier, charges: next });
  };
  const removeChargeItem = (i) => {
    const next = { ...(dossier.charges || {}) };
    next.custom_items = chargeItems.filter((_, idx) => idx !== i);
    setDossier({ ...dossier, charges: next });
  };

  if (loading || !dossier) {
    return <div className="realease-card p-6 text-gray-500" data-testid="financing-loading">Chargement du dossier de financement...</div>;
  }

  // ============ LIVE COMPUTATION (recalcul cote frontend a chaque saisie) ============
  // On combine avec dossier.computed pour les champs serveur (project_monthly_payment).
  const liveCompute = () => {
    const borrowers = dossier.borrowers || [];
    const charges = dossier.charges || {};
    const existing_loans = dossier.existing_loans || [];
    const existing_rentals = dossier.existing_rentals || [];
    const weight = dossier.weight_rental_income !== false;

    let total_taxable = 0, total_bonus = 0, total_other = 0, total_salary_display = 0;
    for (const b of borrowers) {
      const taxable = +b.net_taxable_income || 0;
      const salary = +b.net_salary || 0;
      total_taxable += taxable > 0 ? taxable : salary;
      total_salary_display += salary;
      total_bonus += +b.monthly_bonus || 0;
      total_other += +b.other_income || 0;
    }
    const rental_income_total = existing_rentals.reduce((s, r) => s + (+r.monthly_rent || 0), 0);
    const rental_weight = weight ? 0.8 : 1.0;
    const weighted_rental = rental_income_total * rental_weight;
    const total_monthly_income = total_taxable + total_bonus + total_other + weighted_rental;

    const principal_rp = +charges.principal_residence_cost || 0;
    const charges_custom = (charges.custom_items || []).reduce((s, ci) => s + (+ci.amount || 0), 0);
    const total_monthly_charges = principal_rp + charges_custom;

    const total_existing_loans = existing_loans.reduce((s, ln) => s + (+ln.monthly_payment || 0), 0);
    const total_rental_loans = existing_rentals.reduce((s, r) => s + (+r.loan_monthly || 0), 0);
    const project_monthly_payment = (dossier.computed || {}).project_monthly_payment || 0;
    const total_loan_charges = total_existing_loans + total_rental_loans + project_monthly_payment;
    const endettement_pct = total_monthly_income ? (total_loan_charges / total_monthly_income * 100) : 0;
    const reste_a_vivre = total_monthly_income - total_loan_charges - total_monthly_charges;

    const savings = dossier.savings || {};
    const fixedSavings = ['livret_a', 'ldds', 'lep', 'pel', 'cel', 'assurance_vie', 'pea', 'cto', 'crypto']
      .reduce((s, k) => s + (+savings[k] || 0), 0);
    const customSavings = (savings.custom_items || []).reduce((s, ci) => s + (+ci.amount || 0), 0);
    const total_savings = fixedSavings + customSavings;

    return {
      total_monthly_income, total_taxable_income: total_taxable, total_salary_display,
      total_bonus, total_other_income: total_other,
      total_rental_income: rental_income_total, weighted_rental_income: weighted_rental,
      rental_weight_pct: Math.round(rental_weight * 100),
      total_monthly_charges, principal_residence_cost: principal_rp,
      total_existing_loans, total_rental_loans, project_monthly_payment,
      total_loan_charges, endettement_pct, reste_a_vivre, total_savings,
      nb_borrowers: borrowers.length, nb_existing_loans: existing_loans.length, nb_existing_rentals: existing_rentals.length,
    };
  };

  // Merge serveur + live (live prend la priorite pour les champs recalcules cote frontend)
  const c = { ...(dossier.computed || {}), ...liveCompute() };
  const endPct = c.endettement_pct || 0;
  const endColor = endPct < 30 ? 'text-green-600' : (endPct < 35 ? 'text-amber-600' : 'text-red-600');
  const rav = c.reste_a_vivre || 0;
  const ravColor = rav > 1500 ? 'text-green-600' : (rav > 800 ? 'text-amber-600' : 'text-red-600');

  const borrowers = dossier.borrowers || [];
  const loans = dossier.existing_loans || [];
  const rentals = dossier.existing_rentals || [];

  return (
    <div data-testid="financing-section">
      {/* Toggle / KPI header */}
      <div className="realease-card p-6 cursor-pointer" onClick={() => setOpen(!open)} data-testid="financing-accordion-toggle">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">Dossier de financement</h3>
            <p className="text-xs text-gray-500 mt-1">
              {dossier.exists
                ? `Mis a jour : ${dossier.updated_at ? new Date(dossier.updated_at).toLocaleDateString('fr-FR') : '—'}`
                : 'Non cree - cliquez pour commencer'}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Endettement</p>
              <p className={`text-xl font-bold ${endColor}`}>{endPct.toFixed(2)} %</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Reste a vivre</p>
              <p className={`text-xl font-bold ${ravColor}`}>{fmt(rav)} EUR</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Patrimoine</p>
              <p className="text-xl font-bold text-[#008A8D]">{fmt(c.total_savings)} EUR</p>
            </div>
            <svg className={`w-6 h-6 text-gray-400 transition ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          {/* Actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => save(false)} disabled={saving} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50" data-testid="save-financing-btn">
              {saving ? 'Sauvegarde...' : 'Enregistrer'}
            </button>
            <button onClick={async () => {
              if (!window.confirm("Re-importer depuis la qualification ? Les revenus, credits, charges, epargne et loyers seront remplaces par ceux de la qualification client.")) return;
              try {
                const r = await axios.post(`${API}/financing-dossiers/${projectId}/sync-from-qualification`);
                setDossier(r.data.dossier);
                alert(`Synchronise : ${r.data.counts.borrowers} emprunteur(s), ${r.data.counts.existing_loans} credits, ${r.data.counts.custom_charges} charges, ${r.data.counts.custom_savings} epargnes, ${r.data.counts.existing_rentals} loyers.`);
              } catch (e) { alert('Erreur sync : ' + (e.response?.data?.detail || e.message)); }
            }} className="px-4 py-2 border border-[#A57F4F] text-[#A57F4F] rounded-lg hover:bg-[#A57F4F]/5 font-medium flex items-center gap-1.5" data-testid="sync-from-qualif-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              Re-importer depuis la qualif
            </button>
            <button onClick={() => setShowPreview(true)}
                    className="px-4 py-2 border-2 border-[#008A8D] text-[#008A8D] rounded-lg hover:bg-[#008A8D]/5 font-medium flex items-center gap-1.5"
                    data-testid="preview-financing-pdf">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              Previsualiser
            </button>
            <button onClick={() => downloadPdf('auto')} className="realease-btn-primary" data-testid="download-financing-pdf">
              Telecharger PDF
            </button>
            <button onClick={() => downloadPdf('fallback')}
                    className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-1.5"
                    title="PDF mode pure-Python (toujours dispo, meme si le rendu premium echoue)"
                    data-testid="download-financing-fallback-pdf">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Apercu rapide
            </button>
            <button onClick={sendToClient} className="px-4 py-2 bg-[#E8DCC4] text-[#1A1A1A] rounded-lg hover:bg-[#dcc9a8] font-semibold" data-testid="send-financing-client">
              Envoyer au client
            </button>
            <div className="ml-auto flex items-center gap-2">
              <label className="text-sm text-gray-600">Analyse liee :</label>
              <select value={dossier.analysis_id || ''} onChange={e => updField('analysis_id', e.target.value || null)} className="realease-input text-sm" data-testid="link-analysis">
                <option value="">— Aucune —</option>
                {analyses.map(a => <option key={a.analysis_id} value={a.analysis_id}>{a.name}</option>)}
              </select>
            </div>
          </div>

          {/* Emprunteurs */}
          <Section title="Emprunteurs" testid="borrowers-section">
            {borrowers.map((b, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4 mb-3" data-testid={`borrower-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-wide font-semibold text-[#008A8D]">{i === 0 ? 'Emprunteur principal' : 'Co-emprunteur'}</span>
                  <button onClick={() => removeBorrower(i)} className="text-red-500 text-sm">Retirer</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><label className="realease-label">Prenom</label><TextInput value={b.first_name} onChange={v => updBorrower(i, 'first_name', v)} testid={`b${i}-first-name`} /></div>
                  <div><label className="realease-label">Nom</label><TextInput value={b.last_name} onChange={v => updBorrower(i, 'last_name', v)} /></div>
                  <div><label className="realease-label">Date de naissance</label><input type="date" value={b.birth_date || ''} onChange={e => updBorrower(i, 'birth_date', e.target.value)} className="realease-input text-sm" /></div>
                  <div><label className="realease-label">Email</label><TextInput value={b.email} onChange={v => updBorrower(i, 'email', v)} /></div>
                  <div><label className="realease-label">Telephone</label><TextInput value={b.phone} onChange={v => updBorrower(i, 'phone', v)} /></div>
                  <div>
                    <label className="realease-label">Situation</label>
                    <select value={b.marital_status || ''} onChange={e => updBorrower(i, 'marital_status', e.target.value)} className="realease-input text-sm">
                      <option value="">—</option><option>celibataire</option><option>marie</option><option>pacs</option><option>divorce</option><option>veuf</option>
                    </select>
                  </div>
                  <div><label className="realease-label">Regime matri.</label><TextInput value={b.matrimonial_regime} onChange={v => updBorrower(i, 'matrimonial_regime', v)} placeholder="ex: communaute" /></div>
                  <div><label className="realease-label">Enfants</label><NumInput value={b.children} onChange={v => updBorrower(i, 'children', v)} /></div>
                  <div><label className="realease-label">Profession</label><TextInput value={b.profession} onChange={v => updBorrower(i, 'profession', v)} /></div>
                  <div><label className="realease-label">Employeur</label><TextInput value={b.employer} onChange={v => updBorrower(i, 'employer', v)} /></div>
                  <div>
                    <label className="realease-label">Contrat</label>
                    <select value={b.contract_type || ''} onChange={e => updBorrower(i, 'contract_type', e.target.value)} className="realease-input text-sm">
                      <option value="">—</option><option>CDI</option><option>CDD</option><option>Fonctionnaire</option><option>Independant</option><option>Gerant</option>
                    </select>
                  </div>
                  <div><label className="realease-label">Anciennete (mois)</label><NumInput value={b.seniority_months} onChange={v => updBorrower(i, 'seniority_months', v)} /></div>
                  <div><label className="realease-label">Salaire net / mois</label><NumInput value={b.net_salary} onChange={v => updBorrower(i, 'net_salary', v)} testid={`b${i}-salary`} /></div>
                  <div><label className="realease-label">Net fiscal / mois <span className="text-[10px] text-[#008A8D]">(retenu banque)</span></label><NumInput value={b.net_taxable_income} onChange={v => updBorrower(i, 'net_taxable_income', v)} testid={`b${i}-net-taxable`} /></div>
                  <div><label className="realease-label">Primes / mois</label><NumInput value={b.monthly_bonus} onChange={v => updBorrower(i, 'monthly_bonus', v)} /></div>
                  <div><label className="realease-label">Autres revenus</label><NumInput value={b.other_income} onChange={v => updBorrower(i, 'other_income', v)} /></div>
                  <div className="col-span-full"><label className="realease-label">Label autres revenus</label><TextInput value={b.other_income_label} onChange={v => updBorrower(i, 'other_income_label', v)} placeholder="ex: revenus fonciers, pension..." /></div>
                </div>
              </div>
            ))}
            {borrowers.length < 2 && (
              <button onClick={addBorrower} className="w-full py-3 border-2 border-dashed border-[#008A8D]/30 rounded-lg text-[#008A8D] hover:bg-[#008A8D]/5" data-testid="add-borrower">+ Ajouter un emprunteur</button>
            )}
          </Section>

          {/* Patrimoine / Epargne */}
          <Section title="Patrimoine - Epargne disponible" testid="savings-section">
            <p className="text-xs text-gray-500 mb-3">Ajoutez librement chaque support d'epargne : Livret A, LDDS, PEL, assurance-vie, PEA, CTO, crypto, PER, or physique, etc.</p>
            {savingsItems.length === 0 && (
              <p className="text-sm text-gray-400 italic mb-2">Aucun support ajoute pour le moment.</p>
            )}
            {savingsItems.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`saving-item-${i}`}>
                <div className="col-span-7">
                  <TextInput value={it.label} onChange={v => updSavingItem(i, 'label', v)} placeholder="Ex: Livret A, Assurance vie Generali..." testid={`saving-label-${i}`} />
                </div>
                <div className="col-span-4">
                  <NumInput value={it.amount} onChange={v => updSavingItem(i, 'amount', v)} placeholder="Montant EUR" testid={`saving-amount-${i}`} />
                </div>
                <button onClick={() => removeSavingItem(i)} className="col-span-1 text-red-500 text-sm hover:bg-red-50 rounded py-1" data-testid={`remove-saving-${i}`}>×</button>
              </div>
            ))}
            <button onClick={addSavingItem} className="mt-2 text-sm text-[#008A8D] hover:underline" data-testid="add-saving-item">+ Ajouter un support d'epargne</button>
            <p className="text-right text-sm mt-3 text-gray-600">Total epargne : <b className="text-[#008A8D]">{fmt(c.total_savings)} EUR</b></p>
          </Section>

          {/* Charges mensuelles */}
          <Section title="Charges mensuelles du foyer" testid="charges-section">
            <p className="text-xs text-gray-500 mb-3">
              Ajoutez uniquement les charges retenues par la banque (loyer actuel, pensions, assurances obligatoires, etc.).
              <br/><span className="italic">Les charges courantes (alimentation, transport, loisirs...) ne sont pas prises en compte dans le calcul d'endettement.</span>
            </p>
            {chargeItems.length === 0 && (
              <p className="text-sm text-gray-400 italic mb-2">Aucune charge ajoutee pour le moment.</p>
            )}
            {chargeItems.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`charge-item-${i}`}>
                <div className="col-span-7">
                  <TextInput value={it.label} onChange={v => updChargeItem(i, 'label', v)} placeholder="Ex: Loyer RP, Pension alimentaire..." testid={`charge-label-${i}`} />
                </div>
                <div className="col-span-4">
                  <NumInput value={it.amount} onChange={v => updChargeItem(i, 'amount', v)} placeholder="Montant EUR / mois" testid={`charge-amount-${i}`} />
                </div>
                <button onClick={() => removeChargeItem(i)} className="col-span-1 text-red-500 text-sm hover:bg-red-50 rounded py-1" data-testid={`remove-charge-${i}`}>×</button>
              </div>
            ))}
            <button onClick={addChargeItem} className="mt-2 text-sm text-[#008A8D] hover:underline" data-testid="add-charge-item">+ Ajouter une charge</button>
            <p className="text-right text-sm mt-3 text-gray-600">Total charges : <b className="text-[#008A8D]">{fmt(c.total_monthly_charges)} EUR</b></p>
          </Section>

          {/* Credits en cours */}
          <Section title="Credits en cours (hors projet)" testid="loans-section">
            {loans.map((ln, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`loan-${i}`}>
                <div className="col-span-4"><TextInput value={ln.label} onChange={v => updLoan(i, 'label', v)} placeholder="Ex: Pret auto" /></div>
                <div className="col-span-2">
                  <select value={ln.kind || ''} onChange={e => updLoan(i, 'kind', e.target.value)} className="realease-input text-sm">
                    <option value="">Type</option><option>conso</option><option>auto</option><option>immo</option><option>autre</option>
                  </select>
                </div>
                <div className="col-span-2"><NumInput value={ln.monthly_payment} onChange={v => updLoan(i, 'monthly_payment', v)} placeholder="Mensualite" /></div>
                <div className="col-span-2"><NumInput value={ln.remaining_capital} onChange={v => updLoan(i, 'remaining_capital', v)} placeholder="CRD" /></div>
                <div className="col-span-1"><NumInput value={ln.remaining_months} onChange={v => updLoan(i, 'remaining_months', v)} placeholder="Mois" /></div>
                <button onClick={() => removeLoan(i)} className="col-span-1 text-red-500 text-sm">×</button>
              </div>
            ))}
            <button onClick={addLoan} className="mt-2 text-sm text-[#008A8D] hover:underline" data-testid="add-loan">+ Ajouter un credit</button>
            <p className="text-right text-sm mt-3 text-gray-600">Total mensualites : <b className="text-[#008A8D]">{fmt(c.total_existing_loans)} EUR</b></p>
          </Section>

          {/* Loyers existants */}
          <Section title="Biens deja en location" testid="rentals-section">
            <div className="flex items-center justify-between gap-3 mb-3 p-3 bg-[#008A8D]/5 rounded-lg border border-[#008A8D]/20">
              <div className="text-sm">
                <p className="font-semibold text-gray-800">Ponderation revenus locatifs a 80%</p>
                <p className="text-xs text-gray-500">Regle bancaire standard : seuls 80% des loyers sont retenus (vacance, impayes, charges).</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-testid="toggle-weight-rental">
                <input type="checkbox"
                       checked={dossier.weight_rental_income !== false}
                       onChange={e => updField('weight_rental_income', e.target.checked)}
                       className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-[#008A8D] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>
            {rentals.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`rental-${i}`}>
                <div className="col-span-5"><TextInput value={r.address} onChange={v => updRental(i, 'address', v)} placeholder="Adresse" /></div>
                <div className="col-span-2"><TextInput value={r.property_type} onChange={v => updRental(i, 'property_type', v)} placeholder="Type" /></div>
                <div className="col-span-2"><NumInput value={r.monthly_rent} onChange={v => updRental(i, 'monthly_rent', v)} placeholder="Loyer" /></div>
                <div className="col-span-2"><NumInput value={r.loan_monthly} onChange={v => updRental(i, 'loan_monthly', v)} placeholder="Pret" /></div>
                <button onClick={() => removeRental(i)} className="col-span-1 text-red-500 text-sm">×</button>
              </div>
            ))}
            <button onClick={addRental} className="mt-2 text-sm text-[#008A8D] hover:underline" data-testid="add-rental">+ Ajouter un bien locatif</button>
            <p className="text-right text-sm mt-3 text-gray-600">
              Revenus locatifs / mois : <b className="text-[#008A8D]">{fmt(c.total_rental_income)} EUR</b>
              {' '} — retenu banque : <b className="text-[#008A8D]">{fmt(c.weighted_rental_income)} EUR</b> ({c.rental_weight_pct || 100}%)
            </p>
          </Section>

          {/* Synthese */}
          <div className="realease-card p-5 bg-gradient-to-br from-[#008A8D]/5 to-[#E8DCC4]/30">
            <h4 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Synthese banque</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><p className="text-xs text-gray-500 uppercase">Revenus retenus</p><p className="text-lg font-bold text-[#008A8D]">{fmt(c.total_monthly_income)} EUR</p></div>
              <div><p className="text-xs text-gray-500 uppercase">Mensualite projet</p><p className="text-lg font-bold">{fmt(c.project_monthly_payment)} EUR</p></div>
              <div><p className="text-xs text-gray-500 uppercase">Endettement</p><p className={`text-lg font-bold ${endColor}`}>{endPct.toFixed(2)} %</p></div>
              <div><p className="text-xs text-gray-500 uppercase">Reste a vivre</p><p className={`text-lg font-bold ${ravColor}`}>{fmt(rav)} EUR</p></div>
            </div>
            <p className="text-xs text-gray-500 mt-3">Seuil bancaire standard : endettement &lt;= 35 %. Calcul inclut les revenus locatifs ponderes a {c.rental_weight_pct || 100} %.</p>
          </div>
        </div>
      )}
      {showPreview && (
        <PdfPreviewModal
          url={`${API}/financing-dossiers/${projectId}/pdf`}
          title="Apercu - Dossier de financement"
          downloadName={`RealEase_Dossier_Financement_${projectId}.pdf`}
          entityType="financing"
          entityId={projectId}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};
